// LineEndpointHandlesController (TR6, PRD-F5).
//
// Encapsulates the endpoint-handle UX previously living in
// `o2d-whiteboard.element.ts:addEndpointHandles` (~120 LOC).
//
// T22 wires dragstart/dragmove/dragend with a per-handle snapshot Map
// (FC17 anti-race) and pushes a MoveEndpointCommand on dragend.

import Konva from 'konva';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { BoardState } from '../../core/state/board-state.js';
import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { ObjectRegistry } from '../../core/object-registry.js';
import {
    HANDLE_FILL,
    HANDLE_STROKE,
    SNAP_SCREEN_PX,
} from '../../constants/whiteboard-config.constants.js';
import { createSelectionHandle, sizeSelectionHandle } from '../../core/konva/selection-handle.js';
import { findSnap } from '../../objects/line-anchor.js';
import { isLine } from './line.types.js';
import type { LineObject } from './line.interface.js';
import { MoveEndpointCommand, endpointBounds, type EndpointSnapshot } from './line.commands.js';
import { wireHandleHover } from './line-handle-hover.js';

type Endpoint = 'start' | 'end';

/**
 * Konva layer/node accessors and the readonly probe injected via closure.
 *
 * `selectionLayer` is the existing core layer — handles render alongside the
 * transformer (see TransformerHostController convention). `getReadonly` is
 * a closure rather than a host-type intersection to keep ISP clean and to
 * make the controller trivially mockable in tests.
 */
export interface LineEndpointHandlesDeps {
    /** Home of the Transformer. NOT the handle layer — see `#handleLayer`. */
    selectionLayer(): Konva.Layer | undefined;
    contentLayer(): Konva.Layer | undefined;
    konvaNodeFor(id: string): Konva.Node | undefined;
    overlayLayer(): Konva.Layer | undefined;
    getReadonly(): boolean;
    registry(): ObjectRegistry;
    /**
     * Repaint a line's Konva node from a transient object WITHOUT touching
     * BoardState. Used for the live drag preview: writing state on every
     * `dragmove` frame would flood the undo history with one entry per pixel.
     */
    renderLine(line: LineObject): void;
}

export class LineEndpointHandlesController implements ReactiveController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #deps: LineEndpointHandlesDeps;
    readonly #disconnect$ = new Subject<void>();
    #handles: Konva.Rect[] = [];
    readonly #dragSnapshots = new Map<Endpoint, EndpointSnapshot>();
    /** The line the current handles belong to, or null when none are shown. */
    #lineId: string | null = null;
    /**
     * Endpoints whose handle the pointer currently owns — from `mousedown`
     * until the `dragend` command is pushed.
     *
     * `Konva.Node.isDragging()` is NOT a substitute: Konva registers the drag
     * element on mousedown with `dragStatus: 'ready'` and only promotes it to
     * `'dragging'` once the drag distance is exceeded, then demotes it to
     * `'stopped'` before firing `dragend`. `isDragging()` is therefore false
     * in exactly the two windows where a stray `#syncHandles()` would snap the
     * handle back onto the stale endpoint and make the whole gesture a no-op.
     */
    readonly #draggingEndpoints = new Set<Endpoint>();
    /** BoardState listener teardowns (the bus streams use `#disconnect$`). */
    #stateTeardown: Array<() => void> = [];

    constructor(
        host: ReactiveControllerHost,
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
        deps: LineEndpointHandlesDeps,
    ) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
        this.#deps = deps;
        host.addController(this);
    }

    hostConnected(): void {
        this.#bus.selection$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({
                next: (ids) => this.#onSelectionChanged(ids),
            });

        // Handles are created once per selection, so without these they would
        // stay behind whenever the line moves underneath them:
        //  - liveTransform$ fires on every drag-move frame, BEFORE the state
        //    commits. It carries the dragged object's id, but an anchored line
        //    follows a different object, so we resync on any live transform.
        //  - object-updated covers the committed move plus undo/redo and any
        //    programmatic endpoint change.
        //  - viewport-changed keeps the handle radius screen-constant on zoom.
        this.#bus.liveTransform$
            .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
            .subscribe({ next: () => this.#syncHandles('live') });

        this.#stateTeardown = [
            this.#state.on('object-updated', ({ object }) => {
                if (object.id === this.#lineId) this.#syncHandles('state');
            }),
            this.#state.on('viewport-changed', () => this.#syncHandles('state')),
        ];
    }

    /**
     * The endpoint handles must live ABOVE the line, or Konva's top-down hit
     * test hands the press to the line's stroke (which is draggable) and the
     * whole line moves instead of the one endpoint.
     *
     * `overlayLayer` is the topmost layer — the handles' natural home.
     *
     * (Bis 2026-07-16 stand hier, `selectionLayer` koenne nicht dienen, weil sie
     * absichtlich UNTER `contentLayer` liege. Das gilt nicht mehr: Sie liegt
     * jetzt darueber, weil die Eck-Anfasser sonst unter ueberlappenden Objekten
     * verschwanden. `overlayLayer` bleibt trotzdem richtig — die Endpunkte
     * gehoeren ueber den Transformer, nicht neben ihn.)
     *
     * Der Rueckfall haelt den Controller mit einem minimalen Satz an
     * Abhaengigkeiten benutzbar.
     */
    #handleLayer(): Konva.Layer | undefined {
        return this.#deps.overlayLayer() ?? this.#deps.selectionLayer();
    }

    hostDisconnected(): void {
        this.#removeHandles();
        for (const off of this.#stateTeardown.splice(0)) off();
        this.#disconnect$.next();
        this.#disconnect$.complete();
    }

    /**
     * Move the existing handles back onto their line's endpoints and refresh
     * their screen-constant radius. Cheap enough to run on every drag frame.
     *
     * Two coordinate sources, because the two callers see different truths:
     *
     *  - `'state'` (object-updated, viewport-changed): BoardState has
     *    committed, so `x1/y1/x2/y2` ARE the world positions. Reading them
     *    directly makes this immune to the order in which the host element and
     *    this controller receive `object-updated` — the element's
     *    `applyBaseGeometry` may not have moved the Konva node yet.
     *  - `'live'` (liveTransform$): the Konva node already sits at its new
     *    position while `x`/`x1`/… still hold the pre-drag values, so the
     *    endpoints have to be re-anchored to the node as local offsets.
     *
     * While the pointer owns a handle, this is a strict no-op — see
     * `#draggingEndpoints` for why `isDragging()` cannot be trusted here.
     */
    #syncHandles(source: 'state' | 'live'): void {
        if (this.#draggingEndpoints.size > 0) return;
        if (this.#lineId === null || this.#handles.length === 0) return;
        const obj = this.#state.getObject(this.#lineId);
        if (!obj || !isLine(obj)) return;

        const node = source === 'live' ? this.#deps.konvaNodeFor(this.#lineId) : undefined;
        const originX = node?.x() ?? obj.x;
        const originY = node?.y() ?? obj.y;
        const scale = this.#state.viewport.scale || 1;

        for (const handle of this.#handles) {
            const isStart = handle.name() === 'endpoint-start';
            const localX = (isStart ? obj.x1 : obj.x2) - obj.x;
            const localY = (isStart ? obj.y1 : obj.y2) - obj.y;
            handle.position({ x: originX + localX, y: originY + localY });
            sizeSelectionHandle(handle, scale);
        }
        this.#handleLayer()?.batchDraw();
    }

    /**
     * Recreate handles whenever the selection changes. Single-line
     * selection in non-readonly mode → 2 draggable handles named
     * `endpoint-start` / `endpoint-end`. Anything else → no handles.
     */
    #onSelectionChanged(ids: ReadonlyArray<string>): void {
        // A re-emission of the SAME single line must not tear the handles down
        // and rebuild them: `Konva.Node.destroy()` on a handle the pointer is
        // holding kills the gesture, and `#removeHandles` drops the dragstart
        // snapshot, so `dragend` would push no command at all.
        if (ids.length === 1 && ids[0] === this.#lineId && this.#handles.length > 0) {
            this.#syncHandles('state');
            return;
        }
        this.#removeHandles();
        if (ids.length !== 1) return;
        const obj = this.#state.getObject(ids[0]!);
        if (!obj || !isLine(obj)) return;
        if (this.#deps.getReadonly()) return;
        this.#createHandles(obj);
    }

    /**
     * Render two standard square handles into the selection layer, one per
     * endpoint. Size is screen-constant via `viewport.scale`.
     */
    #createHandles(line: LineObject): void {
        const layer = this.#handleLayer();
        if (!layer) return;
        this.#lineId = line.id;
        const scale = this.#state.viewport.scale || 1;
        for (const ep of ['start', 'end'] as const) {
            const x = ep === 'start' ? line.x1 : line.x2;
            const y = ep === 'start' ? line.y1 : line.y2;
            const handle = createSelectionHandle({ x, y, scale, name: `endpoint-${ep}` });
            this.#wireDrag(handle, ep, line.id);
            wireHandleHover(handle, () => {
                const o = this.#state.getObject(line.id);
                return o && isLine(o) ? o.strokeColor : undefined;
            });
            layer.add(handle);
            this.#handles.push(handle);
        }
        layer.batchDraw();
    }

    /**
     * Attach dragstart/dragmove/dragend listeners to a single endpoint
     * handle. Snapshot is keyed by endpoint in `#dragSnapshots` so a
     * concurrent multi-pointer drag of both handles cannot overwrite
     * each other (FC17 anti-race).
     */
    #wireDrag(handle: Konva.Rect, ep: Endpoint, lineId: string): void {
        // A press that never turns into a drag would otherwise bubble to the
        // stage, where InteractionController finds no BoardObject behind the
        // handle and clears the selection — destroying the handles under the
        // user's cursor.
        handle.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
            e.cancelBubble = true;
        });

        handle.on('dragstart', () => {
            this.#draggingEndpoints.add(ep);
            const obj = this.#state.getObject(lineId);
            if (!obj || !isLine(obj)) return;
            this.#dragSnapshots.set(ep, {
                x1: obj.x1,
                y1: obj.y1,
                x2: obj.x2,
                y2: obj.y2,
                ...(obj.startAnchor !== undefined ? { startAnchor: obj.startAnchor } : {}),
                ...(obj.endAnchor !== undefined ? { endAnchor: obj.endAnchor } : {}),
            });
        });

        // Live preview: repaint the line from the handle's current position on
        // every frame. State stays untouched — the single MoveEndpointCommand
        // on `dragend` is what enters the undo history.
        handle.on('dragmove', () => {
            const obj = this.#state.getObject(lineId);
            if (!obj || !isLine(obj)) return;
            const snap = this.#findSnap(handle.x(), handle.y(), lineId);
            // Make the magnetic moment visible: while inside a target's snap
            // radius the handle takes the line's own colour, and
            // reverts the instant the pointer pulls away.
            this.#setSnapLook(handle, snap !== null, obj.strokeColor);
            const wx = snap?.x ?? handle.x();
            const wy = snap?.y ?? handle.y();
            const endpoints: EndpointSnapshot = {
                x1: ep === 'start' ? wx : obj.x1,
                y1: ep === 'start' ? wy : obj.y1,
                x2: ep === 'end' ? wx : obj.x2,
                y2: ep === 'end' ? wy : obj.y2,
            };
            this.#deps.renderLine({ ...obj, ...endpoints, ...endpointBounds(endpoints) });
        });

        handle.on('dragend', () => {
            // The drag is over — drop the accent look regardless of where it
            // landed. `#syncHandles` below only resets the radius, not the fill.
            this.#setSnapLook(handle, false);
            const from = this.#dragSnapshots.get(ep);
            this.#dragSnapshots.delete(ep);
            if (!from) {
                this.#draggingEndpoints.delete(ep);
                return;
            }
            // Read the dropped position BEFORE releasing the sync lock —
            // anything that resyncs would move the handle off it.
            const snap = this.#findSnap(handle.x(), handle.y(), lineId);
            const wx = snap?.x ?? handle.x();
            const wy = snap?.y ?? handle.y();
            const to: EndpointSnapshot = {
                x1: ep === 'start' ? wx : from.x1,
                y1: ep === 'start' ? wy : from.y1,
                x2: ep === 'end' ? wx : from.x2,
                y2: ep === 'end' ? wy : from.y2,
                ...(ep === 'start'
                    ? snap?.anchor
                        ? { startAnchor: snap.anchor }
                        : {}
                    : from.startAnchor !== undefined
                        ? { startAnchor: from.startAnchor }
                        : {}),
                ...(ep === 'end'
                    ? snap?.anchor
                        ? { endAnchor: snap.anchor }
                        : {}
                    : from.endAnchor !== undefined
                        ? { endAnchor: from.endAnchor }
                        : {}),
            };
            // Release the lock first: the command emits `object-updated`, and
            // that listener is what lands both handles on the committed (and
            // possibly snapped) endpoints.
            this.#draggingEndpoints.delete(ep);
            this.#history.push(new MoveEndpointCommand(lineId, from, to), this.#state);
            this.#syncHandles('state');
        });
    }

    /**
     * Toggle a handle between its resting look and the snapped look. The
     * snapped square takes the LINE's own colour (`accentColor`), so the
     * attached endpoint reads as part of its line. It keeps its size: the
     * resting handle is already as large as the snapped one used to be (Anke,
     * 2026-09-29 — both 14 px, the snap shows by colour alone). The grey-blue
     * resting outline is kept in both states, so even a white or very pale line
     * still shows a defined square. `#syncHandles` does not touch the fill, so
     * the reset on `dragend` has to run through here.
     */
    #setSnapLook(handle: Konva.Rect, snapped: boolean, accentColor?: string): void {
        handle.stroke(HANDLE_STROKE);
        handle.fill(snapped ? accentColor ?? HANDLE_FILL : HANDLE_FILL);
        this.#handleLayer()?.batchDraw();
    }

    /**
     * Look up the nearest valid anchor target inside the snap radius.
     * Radius is screen-constant via `viewport.scale`. Excludes the
     * line itself so an endpoint cannot snap onto its own bbox.
     */
    #findSnap(worldX: number, worldY: number, lineId: string) {
        const scale = this.#state.viewport.scale || 1;
        const worldRadius = SNAP_SCREEN_PX / scale;
        return findSnap(
            this.#state.allObjects(),
            this.#deps.registry(),
            worldX,
            worldY,
            worldRadius,
            lineId,
        );
    }

    #removeHandles(): void {
        this.#dragSnapshots.clear();
        this.#draggingEndpoints.clear();
        this.#lineId = null;
        if (this.#handles.length === 0) return;
        for (const h of this.#handles) h.destroy();
        this.#handles = [];
        this.#handleLayer()?.batchDraw();
    }
}
